/**
 * VÉRAULT Portal — Advisor: Unified Client Profile (LIGHTHOUSE)
 *
 * The operational command center for a single client/household.  Editorial
 * luxury reading-room aesthetic; quiet AI in the margins; everything an
 * advisor needs before placing a call on one screen.
 *
 * Powered by GET /api/clients/:id/profile (Phase 1 aggregator) +
 *           POST /api/clients/:id/ai/summarize  and  /ai/next-actions
 */
import React from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  Mail,
  Phone,
  MapPin,
  Plane,
  Users,
  FileText,
  StickyNote,
  Receipt,
  CreditCard,
  Wand2,
  AlertCircle,
  UserPlus,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle2,
} from "lucide-react";

import {
  Surface,
  Heading,
  EditorialRule,
  KeyValue,
  MetricTile,
  StatusTag,
  PortalButton,
  PageHeader,
  AIWhisper,
  Empty,
  Skeleton,
  PortalBreadcrumb,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";
import { ClientFormsPanel } from "./client/ClientFormsPanel";
import DossierPanel from "./journey/DossierPanel";
import { ProfileTiers } from "./client/ProfileTiers";
import PreCallBriefCard from "./client/PreCallBriefCard";
import PortalAccessCard from "./client/PortalAccessCard";

const { fadeInUp, stagger } = portalMotion;

const TABS = [
  { id: "profile", label: "Profile", icon: null },
  { id: "overview", label: "Overview", icon: null },
  { id: "trips", label: "Trips", icon: Plane },
  { id: "travelers", label: "Travelers", icon: Users },
  { id: "money", label: "Money", icon: CreditCard },
  { id: "bookings", label: "Bookings", icon: Receipt },
  { id: "forms", label: "Forms", icon: FileText },
  { id: "dossier", label: "Dossier", icon: FileText },
  { id: "notes", label: "Notes", icon: StickyNote },
  { id: "portal_access", label: "Portal Access", icon: null },
];

function tripStatusTone(stage) {
  const s = (stage || "").toLowerCase();
  if (s.includes("complete") || s === "booked") return "sage";
  if (s.includes("cancel") || s.includes("changes")) return "terracotta";
  if (s === "approved" || s === "booking_in_progress") return "gold";
  return "neutral";
}

function humanStage(stage) {
  if (!stage) return "—";
  return stage.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ═══════════════════════════════════════════════════════════════════
// Wave 26.7 — Convert consultation lead → draft Experience
// ═══════════════════════════════════════════════════════════════════
function ConvertToExperienceCard({ client, clientId }) {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const convert = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/clients/${clientId}/convert-to-experience`, {});
      if (data?.trip_id) navigate(`/portal/advisor/journeys/${data.trip_id}?tab=overview`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't create the Experience.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface variant="default" padded data-testid="convert-to-experience-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <Heading level="eyebrow">Ready to begin</Heading>
          <p className="mt-1.5 font-serif text-[18px] text-[hsl(var(--foreground))]">
            Turn this consultation into an Experience.
          </p>
          <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] max-w-md">
            We'll scaffold a draft trip pre-filled with {client.full_name?.split(" ").slice(-1)[0] || "their"} brief.
            You can fill in dates + destinations from inside the Journey.
          </p>
          {err && <p className="mt-2 text-[12px] text-[#8B4513]">{err}</p>}
        </div>
        <PortalButton
          variant="primary"
          size="sm"
          icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plane className="size-3.5" />}
          onClick={convert}
          disabled={busy}
          data-testid="convert-to-experience-btn"
        >
          {busy ? "Drafting…" : "Convert to Experience"}
        </PortalButton>
      </div>
    </Surface>
  );
}

// -- AI Concierge note (gold-rule) ---------------------------------
function ConciergeBrief({ clientId, profile }) {
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const run = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.post(`/clients/${clientId}/ai/summarize`);
      setSummary(data.summary || data.brief || "");
    } catch (e) {
      setErr("Couldn't reach the concierge — try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Surface variant="paper" padded className="!p-6 md:!p-7">
      <div className="flex items-start justify-between gap-4">
        <AIWhisper label="Concierge brief" loading={loading} testid="client-ai-brief">
          {summary ? (
            <span>{summary}</span>
          ) : err ? (
            <span className="text-[hsl(var(--accent-terracotta))]">{err}</span>
          ) : (
            <span>
              Three quiet lines on who they are, what's outstanding, and one thing worth noticing —
              ready when you are.
            </span>
          )}
        </AIWhisper>
        <PortalButton
          variant={summary ? "ghost" : "primary"}
          size="sm"
          onClick={run}
          disabled={loading}
          icon={<Wand2 className="size-3.5" strokeWidth={1.5} />}
          data-testid="client-ai-brief-btn"
        >
          {summary ? "Refresh" : "Draft a brief"}
        </PortalButton>
      </div>
    </Surface>
  );
}

// -- Next Actions panel ---------------------------------------------
function NextActions({ clientId }) {
  const [actions, setActions] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/ai/next-actions`);
      setActions(Array.isArray(data.actions) ? data.actions : []);
    } catch {
      setActions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Surface variant="inset" padded>
      <div className="flex items-center justify-between mb-4">
        <Heading level="eyebrow">What to do next</Heading>
        <PortalButton variant="link" size="sm" onClick={run} disabled={loading} data-testid="client-ai-actions-btn">
          {actions ? "Refresh" : loading ? "Considering…" : "Suggest"}
        </PortalButton>
      </div>
      {actions === null ? (
        <p className="font-accent italic text-sm text-[hsl(var(--foreground-secondary))]">
          A short list of the most useful next moves — generated when you ask.
        </p>
      ) : actions.length === 0 ? (
        <p className="font-accent italic text-sm text-[hsl(var(--foreground-secondary))]">
          Nothing pressing right now.
        </p>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="client-next-actions-list">
          {actions.map((a, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                className={`mt-1.5 size-1.5 rounded-full shrink-0 ${
                  a.priority === "high"
                    ? "bg-[hsl(var(--accent-terracotta))]"
                    : a.priority === "medium"
                    ? "bg-[hsl(var(--accent-gold))]"
                    : "bg-[hsl(var(--accent-sage))]"
                }`}
                aria-hidden
              />
              <div className="flex-1 min-w-0">
                <p className="font-body text-[14px] text-[hsl(var(--foreground))]">{a.label}</p>
                {a.reason && (
                  <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-secondary))] mt-0.5">
                    {a.reason}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}

// -- Tab panels -----------------------------------------------------
function TripsPanel({ trips }) {
  if (!trips?.length)
    return <Empty title="No trips yet" hint="When a trip lands here it'll show with dates and status." icon={<Plane className="size-6" strokeWidth={1.25} />} />;
  return (
    <ul className="divide-y divide-[rgba(0,0,0,0.06)]">
      {trips.map((t) => (
        <li key={t.id} className="py-4 flex items-start justify-between gap-4" data-testid={`trip-row-${t.id}`}>
          <div className="min-w-0">
            <Link to={`/portal/advisor/journeys/${t.id}`} className="font-serif text-lg text-[hsl(var(--foreground))] hover:text-[hsl(var(--accent-gold))] transition-colors truncate block">
              {t.internal_trip_name || t.client_trip_title || t.destinations || "Untitled trip"}
            </Link>
            <p className="mt-1 font-body text-[13px] text-[hsl(var(--foreground-secondary))]">
              {fmt.formatDateRange(t.start_date, t.end_date) || "Dates TBD"}
              {t.num_travelers ? ` · ${t.num_travelers} traveler${t.num_travelers > 1 ? "s" : ""}` : ""}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <StatusTag tone={tripStatusTone(t.lifecycle_stage)}>{humanStage(t.lifecycle_stage)}</StatusTag>
            {typeof t.price_total === "number" && t.price_total > 0 && (
              <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                {fmt.money(t.price_total, t.price_currency)}
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function TravelersPanel({ travelers, clientId, onRefresh }) {
  const [addOpen, setAddOpen] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const list = travelers || [];

  const handleDelete = async (t) => {
    const name = t.full_name || t.legal_full_name || "this traveler";
    if (!window.confirm(`Remove ${name} from this household?`)) return;
    try {
      await api.delete(`/travels/travelers/${t.id}`);
      onRefresh?.();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't remove traveler.");
    }
  };

  return (
    <div className="space-y-5" data-testid="client-travelers-panel">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          {list.length} traveler{list.length === 1 ? "" : "s"} in this household
        </p>
        {!addOpen && !editId && (
          <PortalButton
            variant="ghost"
            size="sm"
            icon={<UserPlus className="size-3.5" strokeWidth={1.5} />}
            onClick={() => setAddOpen(true)}
            data-testid="client-add-traveler-btn"
          >
            Add traveler
          </PortalButton>
        )}
      </div>

      {addOpen && (
        <Surface variant="paper" padded className="!p-5">
          <HouseholdTravelerForm
            clientId={clientId}
            onCancel={() => setAddOpen(false)}
            onSaved={() => {
              setAddOpen(false);
              onRefresh?.();
            }}
          />
        </Surface>
      )}

      {list.length === 0 && !addOpen ? (
        <Empty title="No travelers on file" hint="Add household members to keep passport and dietary details together." icon={<Users className="size-6" strokeWidth={1.25} />} />
      ) : (
        <ul className="grid sm:grid-cols-2 gap-4">
          {list.map((t) => (
            <li key={t.id} data-testid={`traveler-${t.id}`}>
              {editId === t.id ? (
                <Surface variant="paper" padded className="!p-5">
                  <HouseholdTravelerForm
                    clientId={clientId}
                    initial={t}
                    onCancel={() => setEditId(null)}
                    onSaved={() => {
                      setEditId(null);
                      onRefresh?.();
                    }}
                  />
                </Surface>
              ) : (
                <div className="rounded-lg border border-[rgba(0,0,0,0.06)] bg-white/50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-serif text-lg text-[hsl(var(--foreground))] truncate">
                        {t.full_name || t.legal_full_name || `${t.first_name || ""} ${t.last_name || ""}`.trim() || "Unnamed"}
                      </p>
                      {t.relationship && (
                        <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">{t.relationship}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => setEditId(t.id)}
                        className="size-7 rounded inline-flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
                        data-testid={`traveler-edit-${t.id}`}
                        aria-label="Edit traveler"
                      >
                        <Pencil className="size-3.5" strokeWidth={1.5} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(t)}
                        className="size-7 rounded inline-flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] hover:bg-[hsl(var(--background-paper))]"
                        data-testid={`traveler-delete-${t.id}`}
                        aria-label="Remove from household"
                      >
                        <Trash2 className="size-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <KeyValue label="Passport" value={t.passport_number ? "On file" : null} />
                    <KeyValue label="Date of birth" value={t.date_of_birth ? fmt.formatDate(t.date_of_birth) : null} />
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HouseholdTravelerForm({ clientId, initial, onCancel, onSaved }) {
  const [fullName, setFullName] = React.useState(initial?.full_name || initial?.legal_full_name || "");
  const [relationship, setRelationship] = React.useState(initial?.relationship || "");
  const [dob, setDob] = React.useState(initial?.date_of_birth || "");
  const [passport, setPassport] = React.useState(initial?.passport_number || "");
  const [passportExpiry, setPassportExpiry] = React.useState(initial?.passport_expiration_date || initial?.passport_expiry || "");
  const [dietary, setDietary] = React.useState(initial?.dietary_needs || "");
  const [busy, setBusy] = React.useState(false);
  const inputCls = "w-full px-3 py-2 text-[14px] bg-[hsl(var(--background))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)]";
  const labelCls = "font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1 block";
  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!fullName.trim() || busy) return;
    setBusy(true);
    try {
      const payload = {
        legal_full_name: fullName.trim(),
        full_name: fullName.trim(),
        relationship: relationship.trim() || undefined,
        date_of_birth: dob || undefined,
        passport_number: passport.trim() || undefined,
        passport_expiration_date: passportExpiry || undefined,
        dietary_needs: dietary.trim() || undefined,
        household_client_id: clientId,
      };
      if (initial?.id) {
        await api.patch(`/travelers/${initial.id}`, payload);
      } else {
        await api.post("/travelers", payload);
      }
      onSaved?.();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Couldn't save traveler.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={handleSave} className="space-y-4" data-testid="household-traveler-form">
      <div>
        <span className={labelCls}>Full name *</span>
        <input className={inputCls} value={fullName} onChange={(e) => setFullName(e.target.value)} required autoFocus data-testid="traveler-form-name" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <span className={labelCls}>Relationship</span>
          <input className={inputCls} value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="Spouse · Child · Friend" />
        </div>
        <div>
          <span className={labelCls}>Date of birth</span>
          <input type="date" className={inputCls} value={dob} onChange={(e) => setDob(e.target.value)} />
        </div>
        <div>
          <span className={labelCls}>Passport #</span>
          <input className={inputCls} value={passport} onChange={(e) => setPassport(e.target.value)} />
        </div>
        <div>
          <span className={labelCls}>Passport expiry</span>
          <input type="date" className={inputCls} value={passportExpiry} onChange={(e) => setPassportExpiry(e.target.value)} />
        </div>
      </div>
      <div>
        <span className={labelCls}>Dietary needs</span>
        <input className={inputCls} value={dietary} onChange={(e) => setDietary(e.target.value)} placeholder="Vegetarian · Gluten-free · Allergies" />
      </div>
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgba(0,0,0,0.05)]">
        <PortalButton variant="ghost" size="sm" type="button" onClick={onCancel} disabled={busy}>Cancel</PortalButton>
        <PortalButton
          variant="primary"
          size="sm"
          type="submit"
          disabled={busy || !fullName.trim()}
          icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : <CheckCircle2 className="size-3.5" strokeWidth={1.5} />}
          data-testid="traveler-form-save"
        >
          {busy ? "Saving" : initial?.id ? "Save changes" : "Add traveler"}
        </PortalButton>
      </div>
    </form>
  );
}

function MoneyPanel({ financials }) {
  if (!financials) return <Empty title="No financial activity" icon={<CreditCard className="size-6" strokeWidth={1.25} />} />;
  const cur = financials.currency || "USD";
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricTile label="Outstanding" value={financials.outstanding_balance} currency={cur} tone={financials.outstanding_balance > 0 ? "warn" : "default"} caption="What they still owe" testid="metric-outstanding" />
      <MetricTile label="Receivable · paid" value={financials.ar?.paid || 0} currency={cur} caption={`${financials.ar?.count || 0} item${(financials.ar?.count || 0) === 1 ? "" : "s"}`} testid="metric-ar-paid" />
      <MetricTile label="Receivable · pending" value={financials.ar?.pending || 0} currency={cur} tone="quiet" testid="metric-ar-pending" />
      <MetricTile label="Booking value" value={financials.booking_value} currency={cur} tone="accent" caption="All bookings, lifetime" testid="metric-booking-value" />
    </div>
  );
}

function BookingsPanel({ bookings }) {
  if (!bookings?.length) return <Empty title="No bookings yet" icon={<Receipt className="size-6" strokeWidth={1.25} />} />;
  return (
    <ul className="divide-y divide-[rgba(0,0,0,0.06)]">
      {bookings.map((b) => (
        <li key={b.id} className="py-4 flex items-start justify-between gap-4" data-testid={`booking-${b.id}`}>
          <div className="min-w-0">
            <p className="font-body text-[14px] text-[hsl(var(--foreground))] truncate">
              {b.vendor || b.description || "Booking"}
            </p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              {b.booking_type || "—"} · {b.confirmation_number || "no confirmation"}
            </p>
          </div>
          <div className="text-right shrink-0">
            {b.actual_cost || b.quoted_cost ? (
              <p className="font-serif text-base text-[hsl(var(--foreground))]">
                {fmt.money(b.actual_cost || b.quoted_cost, b.currency)}
              </p>
            ) : null}
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              {b.status || "draft"}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function FormsPanel({ forms }) {
  if (!forms?.length) return <Empty title="No forms sent" hint="Intake, preferences, passport collection — they'll appear here once issued." icon={<FileText className="size-6" strokeWidth={1.25} />} />;
  return (
    <ul className="divide-y divide-[rgba(0,0,0,0.06)]">
      {forms.map((f) => (
        <li key={f.id} className="py-3 flex items-center justify-between gap-3" data-testid={`form-${f.id}`}>
          <div className="min-w-0">
            <p className="font-body text-[14px]">{f.title || f.form_type || "Form"}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              {f.created_at ? fmt.formatDate(f.created_at) : ""}
            </p>
          </div>
          <StatusTag tone={(f.status || "").toLowerCase() === "completed" ? "sage" : "neutral"}>
            {f.status || "pending"}
          </StatusTag>
        </li>
      ))}
    </ul>
  );
}

function NotesPanel({ notes }) {
  if (!notes?.length) return <Empty title="No notes" hint="Quiet observations and reminders live here." icon={<StickyNote className="size-6" strokeWidth={1.25} />} />;
  return (
    <ul className="flex flex-col gap-4">
      {notes.map((n) => (
        <li key={n.id} className="border-l border-[hsl(var(--accent-gold))]/40 pl-4" data-testid={`note-${n.id}`}>
          <p className="font-accent italic text-[15px] leading-relaxed text-[hsl(var(--foreground))]">
            {n.body || n.content || ""}
          </p>
          <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            {n.created_at ? fmt.formatDate(n.created_at) : ""}
          </p>
        </li>
      ))}
    </ul>
  );
}

// -- Page -----------------------------------------------------------
export default function AdvisorClientProfile() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "profile";
  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [tab, setTab] = React.useState(initialTab);

  const refresh = React.useCallback(() => {
    setLoading(true);
    api
      .get(`/clients/${clientId}/profile`)
      .then(({ data }) => setProfile(data))
      .catch((e) => setError(e?.response?.status === 404 ? "Client not found." : "Couldn't load this client."))
      .finally(() => setLoading(false));
  }, [clientId]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-10 w-2/3" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (error) {
    return (
      <Empty
        title={error}
        icon={<AlertCircle className="size-7" strokeWidth={1.25} />}
        action={
          <PortalButton as={Link} to="/portal/advisor/clients" variant="ghost" size="sm">
            Back to clients
          </PortalButton>
        }
      />
    );
  }
  if (!profile) return null;

  const c = profile.client;
  const cur = profile.financials?.currency || "USD";

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PortalBreadcrumb
          parent={{ to: "/portal/advisor/clients", label: "Clients" }}
          trail={[{ label: c.full_name || "Client" }]}
          exitTo="/"
          exitLabel="Exit to website"
        />
        <PageHeader
          eyebrow={c.client_code ? `Client Suite · ${c.client_code}` : "Client Suite"}
          title={c.full_name || "Unnamed client"}
          lede={c.household_label || c.travel_pace ? `${c.household_label || ""}${c.household_label && c.travel_pace ? " · " : ""}${c.travel_pace || ""}` : null}
          actions={
            <>
              <StatusTag tone={c.status === "active" ? "sage" : "neutral"}>{c.status || "active"}</StatusTag>
              <PortalButton
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/concierge/conversations", { client_id: clientId });
                    navigate(`/portal/advisor/concierge?conv=${data.id}`);
                  } catch {
                    navigate("/portal/advisor/concierge");
                  }
                }}
                data-testid="client-open-concierge"
              >
                Open in Concierge
              </PortalButton>
              <PortalButton
                variant="ghost"
                size="sm"
                icon={<Trash2 className="size-3.5" strokeWidth={1.5} />}
                onClick={async () => {
                  // Wave 26.18 — Delete now offers a two-step path:
                  // first confirm wants Trash (soft, 30d recoverable),
                  // hold-shift skip-to-permanent for advisors who already
                  // confirmed in the UI flow.
                  const permanent = window.event && window.event.shiftKey;
                  const verb = permanent ? "PERMANENTLY DELETE" : "Move to Trash";
                  const detail = permanent
                    ? `This will permanently delete ${c.full_name || "this client"} AND their trips, travelers, and conversations. This cannot be undone.\n\nType DELETE to confirm.`
                    : `Move ${c.full_name || "this client"} to Trash?\n\nThe client and their trips will be hidden from active lists for 30 days, then permanently purged. Restore from Settings · Trash before then.\n\n(Hold Shift while clicking Delete to bypass Trash.)`;
                  if (permanent) {
                    const ans = window.prompt(detail, "");
                    if (ans !== "DELETE") return;
                  } else {
                    if (!window.confirm(detail)) return;
                  }
                  try {
                    await api.delete(`/travels/clients/${clientId}${permanent ? "?hard=true" : ""}`);
                    // Wave 26.18 — broadcast so other open tabs/views refresh
                    window.dispatchEvent(new CustomEvent("verault:clients-changed"));
                    navigate("/portal/advisor/clients", { replace: true });
                  } catch (e) {
                    window.alert(e?.response?.data?.detail || `Couldn't ${verb.toLowerCase()}.`);
                  }
                }}
                data-testid="client-delete-btn"
                title="Move to Trash · hold Shift to delete permanently"
              >
                Delete
              </PortalButton>
            </>
          }
        />
      </motion.div>

      {/* Identity strip */}
      <motion.div variants={fadeInUp}>
        <Surface variant="default">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <KeyValue label="Email" value={c.email ? <a className="hover:text-[hsl(var(--accent-gold))]" href={`mailto:${c.email}`}>{c.email}</a> : null} testid="kv-email" />
            <KeyValue label="Phone" value={c.phone ? <a className="hover:text-[hsl(var(--accent-gold))]" href={`tel:${c.phone}`}>{c.phone}</a> : null} testid="kv-phone" />
            <KeyValue label="Home airport" value={c.home_airport} testid="kv-airport" />
            <KeyValue label="Budget" value={c.budget_range} testid="kv-budget" />
          </div>
        </Surface>
      </motion.div>

      {/* Concierge row */}
      <motion.div variants={fadeInUp} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ConciergeBrief clientId={clientId} profile={profile} />
        </div>
        <NextActions clientId={clientId} />
      </motion.div>

      {/* Metrics */}
      <motion.div variants={fadeInUp}>
        <EditorialRule label="At a glance" />
        <div className="mt-6">
          <MoneyPanel financials={profile.financials} />
        </div>
      </motion.div>

      {/* Tabbed content */}
      <motion.div variants={fadeInUp}>
        <EditorialRule label="The studio" />
        <nav className="mt-5 flex flex-wrap gap-1 sm:gap-2" data-testid="profile-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`group relative px-3 py-2 font-body text-[13px] tracking-tight transition-colors duration-200 ${
                tab === t.id
                  ? "text-[hsl(var(--foreground))]"
                  : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <span className={tab === t.id ? "font-serif" : ""}>{t.label}</span>
              <span
                className={`absolute -bottom-px left-3 right-3 h-px transition-colors duration-300 ${
                  tab === t.id ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"
                }`}
                aria-hidden
              />
            </button>
          ))}
        </nav>
        <div className="mt-6">
          {tab === "profile" ? (
            <div className="space-y-6">
              {c.consultation_status === "scheduled" && (
                <ConvertToExperienceCard client={c} clientId={c.id} />
              )}
              {c.consultation_status === "scheduled" && (
                <PreCallBriefCard client={c} clientId={c.id} onUpdated={refresh} />
              )}
              <ProfileTiers client={c} clientId={c.id} onRefresh={refresh} />
            </div>
          ) : (
          <Surface variant="default" padded>
            {tab === "overview" && (
              <div className="space-y-6">
                <KeyValue label="Notes from intake" value={c.notes || c.preferences || null} valueFont="serif" />
                <div className="grid sm:grid-cols-2 gap-6">
                  <KeyValue label="Dietary" value={c.dietary_needs} />
                  <KeyValue label="Celebrations" value={c.celebration_dates} />
                  <KeyValue label="Travel pace" value={c.travel_pace} />
                  <KeyValue label="Loyalty programs" value={Array.isArray(c.loyalty_programs) ? c.loyalty_programs.join(", ") : c.loyalty_programs} />
                </div>
              </div>
            )}
            {tab === "trips" && <TripsPanel trips={profile.trips} />}
            {tab === "travelers" && (
              <TravelersPanel
                travelers={profile.travelers}
                clientId={c.id}
                onRefresh={refresh}
              />
            )}
            {tab === "money" && <MoneyPanel financials={profile.financials} />}
            {tab === "bookings" && <BookingsPanel bookings={profile.bookings} />}
            {tab === "forms" && <ClientFormsPanel clientId={clientId} />}
            {tab === "dossier" && <DossierPanel clientId={clientId} scope="client" />}
            {tab === "notes" && <NotesPanel notes={profile.notes} />}
            {tab === "portal_access" && (
              <PortalAccessCard
                clientId={clientId}
                defaultEmail={c.email}
                clientName={c.full_name}
              />
            )}
          </Surface>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
